<template>
  <div class="v3-data-table periods-page">
    <div class="py-3 periods-page__alert">
      <InfoBox
        v-if="state.serverRes"
        :class="{ danger: state.hasError }"
        :title="state.serverRes.title"
      >
        {{ state.serverRes.message }}
      </InfoBox>
    </div>
    <div
      v-if="state.reOrderedPeriods"
      class="position-relative main-datatable-container table-responsive periods-page__datatable-wrap"
    >
      <!-- Top horizontal scroller (synced with main table scroller — same pattern as DataTable / reports). -->
      <CustomScrollbar
        :scroller-id="periodsTopScrollId"
        :settings="{ suppressScrollY: true }"
        class="gradient-scrollbar periods-page__top-scroller h-auto"
        :style="periodsTopScrollerStyle"
        aria-hidden="true"
        @ps-scroll-x="onPeriodsTableScrollX"
      >
        <div
          :style="{
            width: `${periodsTableScrollWidth}px`,
            height: '13px',
            minWidth: '100%'
          }"
        />
      </CustomScrollbar>
      <CustomScrollbar
        :scroller-id="periodsMainScrollId"
        :settings="{ suppressScrollY: true }"
        class="gradient-scrollbar periods-page__main-scroller"
        :keyboard-accessible="true"
        scroll-region-label="Periods table"
        @ps-scroll-x="onPeriodsTableScrollX"
      >
        <table
          :id="periodsTableElementId"
          class="table bg-white periods-page__table mb-0"
        >
          <thead>
            <tr>
              <th class="period-table-th position-relative" />
              <th class="period-table-th position-relative">
                <div>Name</div>
              </th>
              <th class="period-table-th position-relative">
                <div>Status</div>
              </th>
              <th
                class="period-table-th position-relative periods-page__col-actions"
              >
                <div class="text-end">Action</div>
              </th>
            </tr>
          </thead>
          <draggable
            v-model="state.reOrderedPeriods"
            item-key="id"
            filter=".no-drag"
            tag="tbody"
            @end="saveNewPeriodsOrder"
          >
            <template #item="{ element, index }">
              <tr class="w-100 bg-white">
                <td class="periods-page__drag-col mw-auto text-center">
                  <div
                    class="d-flex align-items-center justify-content-center gap-2 px-2 py-1 rounded"
                  >
                    <i class="ri-drag-move-2-line text-value-xl"></i>
                    <IconButton
                      :icon="'ri-arrow-up-s-line'"
                      :disabled="index === 0"
                      :aria-label="'Move period up'"
                      :tooltip="'Move period up'"
                      @click="moveUp(index)"
                    />
                    <IconButton
                      :icon="'ri-arrow-down-s-line'"
                      :disabled="index === state.reOrderedPeriods.length - 1"
                      :aria-label="'Move period down'"
                      :tooltip="'Move period down'"
                      @click="moveDown(index)"
                    />
                  </div>
                </td>
                <td>{{ element.name }}</td>
                <td>
                  <LabelPill
                    :class="`status-${element.status ? 'active' : 'inactive'}`"
                    :icon="
                      element.status
                        ? 'ri-checkbox-circle-fill'
                        : 'ri-close-circle-fill'
                    "
                  >
                    {{ element.status ? "Active" : "Inactive" }}
                  </LabelPill>
                </td>
                <td class="periods-page__col-actions">
                  <div class="text-end">
                    <Actions
                      :actions="tableActions"
                      @click-action="
                        handleTableAction($event, element, index + 1)
                      "
                    ></Actions>
                  </div>
                </td>
              </tr>
            </template>
          </draggable>
        </table>
      </CustomScrollbar>
    </div>
  </div>
</template>

<script>
import CreatePeriodForm from "@/v3components/Forms/CreatePeriodForm.vue"
import InfoBox from "@/v3components/shared/Alerts/InfoBox.vue"
import IconButton from "@/v3components/shared/Buttons/IconButton.vue"
import LabelPill from "@/v3components/shared/Buttons/LabelPill.vue"
import Actions from "@/v3components/shared/DataTable/Actions.vue"
import CustomScrollbar from "@/v3components/CustomScrollbar.vue"
import {
  computed,
  inject,
  nextTick,
  onMounted,
  onUnmounted,
  reactive,
  ref,
  watch
} from "vue"
import draggable from "vuedraggable"
import { useStore } from "vuex"

export default {
  components: {
    draggable,
    Actions,
    CustomScrollbar,
    LabelPill,
    IconButton,
    InfoBox
  },
  setup() {
    const scrollSuffix =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `p-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
    const periodsTopScrollId = `periodsTableTopScroll-${scrollSuffix}`
    const periodsMainScrollId = `periodsTableMainScroll-${scrollSuffix}`
    const periodsTableElementId = `periods-table-${scrollSuffix}`

    const periodsTableScrollWidth = ref(0)
    let topScrollEl = null
    let mainScrollEl = null
    let tableEl = null

    const periodsTopScrollerStyle = computed(() => ({
      display: periodsTableScrollWidth.value > 0 ? "block" : "none"
    }))

    const syncPeriodsScrollElements = () => {
      if (typeof document === "undefined") return
      topScrollEl = document.getElementById(periodsTopScrollId)
      mainScrollEl = document.getElementById(periodsMainScrollId)
      tableEl = document.getElementById(periodsTableElementId)
      if (tableEl) {
        periodsTableScrollWidth.value = tableEl.offsetWidth
      }
    }

    const forcePeriodsScrollbarSync = () => {
      syncPeriodsScrollElements()
      const nudge = (el) => {
        if (!el) return
        const s = el.scrollLeft
        el.scrollLeft = s + 1
        nextTick(() => {
          el.scrollLeft = s
        })
      }
      nudge(mainScrollEl)
      nudge(topScrollEl)
    }

    const onPeriodsTableScrollX = (e) => {
      if (e?.target?.scrollLeft === undefined) return
      syncPeriodsScrollElements()
      const scrollLeft = e.target.scrollLeft
      const sid = e.target.id
      if (sid === periodsTopScrollId && mainScrollEl) {
        mainScrollEl.scrollLeft = scrollLeft
      } else if (sid === periodsMainScrollId && topScrollEl) {
        topScrollEl.scrollLeft = scrollLeft
      }
      if (tableEl) {
        periodsTableScrollWidth.value = tableEl.offsetWidth
      }
    }

    let resizeTimeout = null
    const onPeriodsTableResize = () => {
      if (resizeTimeout) clearTimeout(resizeTimeout)
      resizeTimeout = setTimeout(() => {
        syncPeriodsScrollElements()
        nextTick(() => {
          forcePeriodsScrollbarSync()
        })
      }, 100)
    }

    const store = useStore()
    const state = reactive({
      savingPeriods: false,
      reOrderedPeriods: [],
      serverRes: null,
      hasError: false
    })

    const actionDialog = inject("actionDialog")
    const modal = inject("modal")

    const periods = computed(() => store.getters["periods/periods"])

    const onDeletePeriod = (item) => {
      actionDialog.open(deletePeriod, {
        args: item,
        props: { danger: true, title: "Delete", question: "Are you sure?" }
      })
    }

    const onEditPeriod = (item) => {
      store.commit("periods/SET_EDITABLE_PERIOD", item)
      showPeriodForm(item)
    }

    const showPeriodForm = () => {
      modal.open(CreatePeriodForm, {
        size: "lg",
        title: "Edit period",
        props: {
          editableMode: true
        }
      })
    }

    const deletePeriod = (period) => {
      store
        .dispatch("periods/deletePeriod", { periodId: period.id })
        .then(() => {
          setTimeout(() => {
            store.dispatch("periods/getPeriods", {
              clearCacheEntry: true
            })
          }, 750)
        })
    }

    const setResponseInfoBox = (title, message, isError = false) => {
      if (title || message) {
        state.serverRes = { title, message }
        state.hasError = isError
      } else {
        state.serverRes = null
        state.hasError = false
      }
    }

    const saveNewPeriodsOrder = async () => {
      if (state.savingPeriods) return

      state.savingPeriods = true
      const data = state.reOrderedPeriods.map((period, i) => {
        return {
          id: period.id,
          position: i + 1
        }
      })
      try {
        await store.dispatch("periods/updatePosition", data)
        setResponseInfoBox("Success", "Period order updated successfully!")
        setTimeout(() => setResponseInfoBox(), 2000)
      } catch (err) {
        const message =
          err?.response?.data?.message || "Failed to update period order"
        setResponseInfoBox("Error", message, true)
        setTimeout(() => setResponseInfoBox(), 3000)
      } finally {
        state.savingPeriods = false
      }
    }

    const moveUp = (index) => {
      if (index > 0) {
        ;[state.reOrderedPeriods[index], state.reOrderedPeriods[index - 1]] = [
          state.reOrderedPeriods[index - 1],
          state.reOrderedPeriods[index]
        ]
        saveNewPeriodsOrder()
      }
    }

    const moveDown = (index) => {
      if (index < state.reOrderedPeriods.length - 1) {
        ;[state.reOrderedPeriods[index], state.reOrderedPeriods[index + 1]] = [
          state.reOrderedPeriods[index + 1],
          state.reOrderedPeriods[index]
        ]
        saveNewPeriodsOrder()
      }
    }

    watch(
      () => periods.value,
      () => {
        state.reOrderedPeriods = periods.value ? [...periods.value] : []
      }
    )

    watch(
      () => state.reOrderedPeriods,
      () => {
        nextTick(() => {
          syncPeriodsScrollElements()
          forcePeriodsScrollbarSync()
        })
      },
      { deep: true }
    )

    watch(periodsTableScrollWidth, (w) => {
      if (w > 0) {
        nextTick(() => {
          forcePeriodsScrollbarSync()
        })
      }
    })

    const tableActions = [
      { label: "Edit", icon: "ri-checkbox-circle-line", action: "edit" },
      {
        label: "Delete",
        icon: "ri-delete-bin-line",
        class: "important",
        action: "delete"
      }
    ]
    const handleTableAction = (action, item) => {
      if (action.action === "delete") {
        onDeletePeriod(item)
      } else {
        onEditPeriod(item)
      }
    }

    onMounted(() => {
      window.addEventListener("resize", onPeriodsTableResize)
      store.dispatch("periods/getPeriods").then(() => {
        if (periods.value) {
          state.reOrderedPeriods = [...periods.value]
        }
        nextTick(() => {
          syncPeriodsScrollElements()
          forcePeriodsScrollbarSync()
        })
      })
    })

    onUnmounted(() => {
      window.removeEventListener("resize", onPeriodsTableResize)
      if (resizeTimeout) clearTimeout(resizeTimeout)
    })

    return {
      periods,
      saveNewPeriodsOrder,
      state,
      tableActions,
      handleTableAction,
      moveUp,
      moveDown,
      periodsTopScrollId,
      periodsMainScrollId,
      periodsTableElementId,
      periodsTableScrollWidth,
      periodsTopScrollerStyle,
      onPeriodsTableScrollX
    }
  }
}
</script>

<style lang="scss" scoped>
.periods-page.v3-data-table {
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;
  padding: 0 8px !important;
}

.periods-page__alert {
  min-width: 0;
  max-width: 100%;
}

.periods-page__datatable-wrap {
  min-width: 0;
  max-width: 100%;

  :deep(.custom-scrollbar-container) {
    width: 100%;
    max-width: 100%;
  }

  /* Keep Perfect Scrollbar horizontal rails visible (default can look absent at high zoom). */
  :deep(.ps__rail-x) {
    opacity: 0.88 !important;
  }

  :deep(.ps.ps--active-x .ps__rail-x),
  :deep(.ps:hover .ps__rail-x) {
    opacity: 1 !important;
  }

  :deep(.periods-page__top-scroller .ps) {
    min-height: 14px;
  }

  :deep(.periods-page__main-scroller .ps) {
    min-height: 0;
  }
}

.periods-page__drag-col {
  width: 100px;
}

.periods-page :deep(table.periods-page__table) {
  /* Wider than the viewport when needed so columns (incl. Action) stay usable; scroll like DataTable reports. */
  width: max-content;
  min-width: 100%;
  table-layout: auto;

  thead {
    tr:first-child {
      th:first-child {
        &::after {
          border-right: 0 !important;
        }
      }
    }
  }

  th,
  td {
    vertical-align: middle;
  }

  .periods-page__col-actions {
    width: 1%;
    min-width: 3.75rem;
    white-space: nowrap;
  }
}

@media (max-width: 480px) {
  .periods-page__drag-col {
    width: auto;
    max-width: 7.5rem;
  }

  .periods-page :deep(table.periods-page__table) {
    th:not(.periods-page__col-actions),
    td:not(.periods-page__col-actions) {
      padding-left: 0.5rem;
      padding-right: 0.5rem;
    }

    th.period-table-th div,
    td:not(.periods-page__col-actions) {
      overflow-wrap: anywhere;
      word-break: break-word;
    }

    .d-flex.gap-2 {
      flex-wrap: wrap;
      justify-content: center;
      gap: 0.25rem !important;
    }
  }
}
</style>
